'use client'

import { useState, useCallback } from 'react'
import { useExtracted } from 'next-intl'
import { ChevronDown } from '@/components/ui/icons'
import { FAQS } from '@/lib/marketing-config'

export function FAQSection() {
  const t = useExtracted()
  const [openId, setOpenId] = useState<string | null>(null)
  const faqCopy = {
    'faq-veo-3-premiere': {
      question: t('Can I use Veo 3 with my current editing software?'),
      answer: t(
        'Yes. Martini is a web-based editor that lets you generate Veo 3 shots directly in the browser. You can then export your project as XML and import it seamlessly into Adobe Premiere Pro, DaVinci Resolve, or any other editing software.',
      ),
    },
    'faq-kling-start-end': {
      question: t('Does Martini support start and end frames with Kling?'),
      answer: t(
        'Yes. Kling 3.0 Pro, Kling O3 Pro, and Kling O1 all support start and end frame control, enabling tight in/out precision for editorial workflows. This allows you to match your AI-generated content exactly to your existing shots for seamless continuity.',
      ),
    },
    'faq-supported-models': {
      question: t('What AI video models does Martini support?'),
      answer:
        'Martini supports leading AI video generation models including Veo 3.1, Kling 3.0 Pro, Kling O3 Pro, Kling 2.6, Kling O1, Sora 2 Pro, MiniMax H3, Seedance 2.5, Boreal by Creatify, Wan 3.0 Prime, Wan 3.0, Hailuo 02, Seedance 1.5, and Grok Imagine. For still image generation and editing, we support Nano Banana Pro and Flux 2 Max. All models are available directly in the Martini web editor, allowing you to choose the best model for each shot in a single unified interface.',
    },
    'faq-still-image': {
      question: t('Does Martini support still image generation?'),
      answer: t(
        'Yes. Martini supports Nano Banana Pro for high-quality image generation with excellent typography and multi-image blending, and Flux 2 Max for flexible aspect ratios and creative editing with reference images. Generate reference frames directly in your timeline.',
      ),
    },
    'faq-editors': {
      question: t('What video editors work with Martini?'),
      answer: t(
        'Martini works with any professional video editor that supports XML import, including Adobe Premiere Pro and DaVinci Resolve. Simply create in our web editor and export your timeline to XML. This gives you the flexibility of a cloud-based AI studio with the power of your preferred desktop NLE.',
      ),
    },
    'faq-end-frame': {
      question: t('Does Martini support end frame control?'),
      answer:
        'Yes. End frame control is available with Veo 3.1, Kling 3.0 Pro, Kling O3 Pro, Kling O1, Hailuo 02, Seedance 1.5, Seedance 2.5, MiniMax H3, Wan 3.0 Prime, and Wan 3.0, giving you precise control over both the start and end frames of your AI-generated videos for tighter editorial workflows. This feature is essential for matching existing shots and maintaining continuity in professional productions.',
    },
    'faq-differentiators': {
      question: t('What makes Martini different from other AI video tools?'),
      answer: t(
        'Martini combines a professional timeline interface with AI generation in the browser. Instead of managing scattered video files, you build your sequence in our web editor and export to XML, seamlessly bridging the gap between AI generation and professional post-production.',
      ),
    },
    'faq-partnership': {
      question: t('Do you have a creative partnership program?'),
      answer: t(
        'Yes. We offer a creative partnership program for studios, agencies, and independent creators working on notable projects. Partners receive priority support, early access to new features, collaborative opportunities, and custom integrations. Contact us at koh@c47.studio to learn more about partnership opportunities.',
      ),
    },
  } as const
  const localizedFaqs = FAQS.map(faq => ({ ...faq, ...(faqCopy[faq.id as keyof typeof faqCopy] ?? {}) }))

  const toggleFAQ = useCallback((id: string) => {
    setOpenId(current => (current === id ? null : id))
  }, [])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, id: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        toggleFAQ(id)
      }
    },
    [toggleFAQ],
  )

  return (
    <section className="py-12" aria-labelledby="faq-heading">
      <h2 id="faq-heading" className="mb-8 text-3xl font-bold tracking-tight md:text-4xl">
        {t('Frequently Asked Questions')}
      </h2>
      <div className="space-y-3" role="list">
        {localizedFaqs.map(faq => {
          const isOpen = openId === faq.id
          return (
            <div
              key={faq.id}
              id={faq.id}
              className="rounded-xl border border-white/20 bg-white/5 backdrop-blur-xl transition-all hover:border-white/25 hover:bg-white/[0.08]"
              role="listitem"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                onKeyDown={e => handleKeyDown(e, faq.id)}
                aria-expanded={isOpen}
                aria-controls={`${faq.id}-content`}
                className="flex w-full cursor-pointer items-center justify-between px-6 py-4 text-start text-base font-medium text-white md:text-lg"
              >
                <span className="pe-4">{faq.question}</span>
                <ChevronDown
                  className={`h-5 w-5 flex-shrink-0 text-white/60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>
              <div
                id={`${faq.id}-content`}
                role="region"
                aria-labelledby={faq.id}
                className={`grid transition-all duration-200 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
              >
                <div className="overflow-hidden">
                  <p className="px-6 pb-4 text-sm leading-relaxed text-white/70 md:text-base">{faq.answer}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
